import clsx from "clsx";
import { round } from "lodash-es";
import { ReactNode } from "react";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";

import { Tooltip } from "mds/components/Tooltip";
import { CircleErrorRedIcon, CircleSuccessGreenIcon, CircleWarningOrangeIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectMonitoringStats } from "store/selectors";
import { getParticipantCountByCourseId, getStatsByCourseIdAndType } from "utils/monitoring";

type TDPropTypes = {
  conditionForDanger?: boolean;
  conditionForInfo?: boolean;
  conditionForWarning?: boolean;
  noWrap?: boolean;
  scrollX?: boolean;
  onClick?: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
};

export const TD = ({
  conditionForDanger,
  conditionForInfo,
  conditionForWarning,
  noWrap,
  scrollX,
  onClick,
  title,
  children,
  className,
  dataTestId,
}: TDPropTypes) => {
  const cName = clsx(
    {
      "scroll-x": scrollX,
      "tooltip-container": title,
    },
    className,
  );
  return (
    <td
      className={cName}
      data-testid={dataTestId}
      style={{
        whiteSpace: noWrap ? "nowrap" : "normal",
        padding: "0.3rem",
        backgroundColor: conditionForDanger
          ? "#f5c6cb"
          : conditionForWarning
            ? "#ffeeba"
            : conditionForInfo
              ? "#bee5eb"
              : undefined,
      }}
      title={title}
      onClick={onClick}
    >
      {children}
      {/* {title ? <span>{title}</span> : null} */}
    </td>
  );
};

export const TDStatusIconWithTooltip = ({
  conditionForDanger = false,
  conditionForWarning = false,
  children,
  dataTestId,
}: {
  conditionForDanger?: boolean;
  conditionForWarning?: boolean;
  children: ReactNode;
  dataTestId?: string;
}) => {
  if (!children) return <TD dataTestId={dataTestId}> </TD>;
  return (
    <TD
      className="center"
      conditionForDanger={conditionForDanger}
      conditionForWarning={conditionForWarning}
      dataTestId={dataTestId}
    >
      <Tooltip text={children}>
        {conditionForDanger ? (
          <CircleErrorRedIcon />
        ) : conditionForWarning ? (
          <CircleWarningOrangeIcon />
        ) : (
          <CircleSuccessGreenIcon />
        )}
      </Tooltip>
    </TD>
  );
};

export const PageLoadStatsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const nParticipants = getParticipantCountByCourseId(stats, courseId);
  const { mean } = getStatsByCourseIdAndType(stats, courseId, MonitoringEventTypeType.ENTER_CLASS);
  return mean ? (
    <TD
      conditionForDanger={mean > 2 && nParticipants > 2}
      conditionForWarning={mean > 1.2 && nParticipants > 2}
      dataTestId="page-load-stats"
    >
      {`${round(mean, 2)}`}
    </TD>
  ) : (
    <TD dataTestId="page-load-stats">-</TD>
  );
};

export const WebsocketDisconnectionsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.WEBSOCKET_DISCONNECTED,
  );
  return <TD dataTestId="websocket-disconnections-stats">{formattedMean}</TD>;
};

export const FailedApiRequestsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.FAILED_API_REQUEST,
  );
  return <TD dataTestId="failed-api-requests-stats">{formattedMean}</TD>;
};

export const PageWorksheetLoadErrorTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.PAGE_WORKSHEET_LOAD_ERROR,
  );
  return <TD dataTestId="page-worksheet-load-error-stats">{formattedMean}</TD>;
};

export const PoorConnectionWarningTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.POOR_CONNECTION_WARNING,
  );
  return <TD dataTestId="poor-connection-warning-stats">{formattedMean}</TD>;
};

export const PreclassCheckFailStatsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.PRECLASS_CHECK_FAIL,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 0.4) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 30 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.25) || (!!percentAffectedUsers && percentAffectedUsers >= 20)
      }
      dataTestId="preclass-check-fail-stats"
    >
      {formattedMean}
    </TD>
  );
};

export const AvRefreshStatsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.AV_REFRESH,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 0.6) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 30 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.3) || (!!percentAffectedUsers && percentAffectedUsers >= 20)
      }
      dataTestId="av-refresh-stats"
    >
      {formattedMean}
    </TD>
  );
};

export const ReloadStreamStatsTD = ({ courseId }: { courseId: string }) => {
  const stats = useAppSelector(selectMonitoringStats);
  const { formattedMean, mean, percentAffectedUsers, nAffectedUsers } = getStatsByCourseIdAndType(
    stats,
    courseId,
    MonitoringEventTypeType.RELOAD_STREAM,
    true,
  );
  return (
    <TD
      conditionForDanger={
        (mean && mean >= 1.25) ||
        (!!percentAffectedUsers && percentAffectedUsers >= 50 && nAffectedUsers > 3)
      }
      conditionForWarning={
        (mean && mean >= 0.5) || (!!percentAffectedUsers && percentAffectedUsers >= 40)
      }
      dataTestId="reload-stream-stats"
    >
      {formattedMean}
    </TD>
  );
};
